import React, { useState, useEffect } from 'react';
import { getTokenForRequest } from '../../utils/apiAuth';

/**
 * TopicSearchModal Component
 * Replaces the basic prompt() dialog for selecting topics to add to weeks
 * Provides a proper searchable interface for topic selection
 */
const TopicSearchModal = ({ show, onHide, week, lessonPlanWeeks = [], onTopicSelected }) => {
  // State management
  const [topics, setTopics] = useState([]);
  const [filteredTopics, setFilteredTopics] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load topics when modal is shown
  useEffect(() => {
    if (show && week) {
      loadAvailableTopics();
    }
  }, [show, week]);

  // Filter topics based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredTopics(topics);
    } else {
      const filtered = topics.filter(topic => 
        topic.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (topic.description && topic.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredTopics(filtered);
    }
  }, [searchTerm, topics]);

  /**
   * Load available topics from the network API
   */
  const loadAvailableTopics = async () => {
    try {
      setLoading(true);
      setError(null);

      const tokenForRequest = getTokenForRequest();
      const encodedToken = window.encodeToken ? window.encodeToken(tokenForRequest) : tokenForRequest;

      const response = await fetch(`./webservices/Network.svc/GetNetwork?token=${encodedToken}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to load topics');
      }

      const networkData = await response.json();
      console.log('Network data received for topic search:', networkData);

      // Extract topics from response
      let topicsData = [];
      if (networkData && networkData.d && networkData.d.nodes) {
        topicsData = networkData.d.nodes;
      } else if (networkData && networkData.nodes) {
        topicsData = networkData.nodes;
      } else if (Array.isArray(networkData)) {
        topicsData = networkData;
      }

      console.log('Available topics for selection:', topicsData);
      
      // Filter out topics that already exist in the lesson plan
      const existingTopicIds = new Set();
      if (lessonPlanWeeks && Array.isArray(lessonPlanWeeks)) {
        lessonPlanWeeks.forEach(week => {
          if (week.Topics && Array.isArray(week.Topics)) {
            week.Topics.forEach(topic => {
              const topicId = String(topic.TopicID || topic.topic_id || topic.ID || topic.id);
              if (topicId) {
                existingTopicIds.add(topicId);
              }
            });
          }
        });
      }
      
      const availableTopics = topicsData.filter(topic => {
        const topicId = String(topic.ID || topic.id || topic.Id);
        return !existingTopicIds.has(topicId);
      });
      
      console.log(`Filtered topics: ${availableTopics.length} available out of ${topicsData.length} total (${topicsData.length - availableTopics.length} already in lesson plan)`);
      setTopics(availableTopics);
      setFilteredTopics(availableTopics);

    } catch (err) {
      console.error('Error loading topics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle topic selection
   */
  const handleTopicSelect = async (selectedTopic) => {
    console.log('Topic selected from modal:', selectedTopic);
    if (onTopicSelected) {
      // Call the handler and wait for it to complete
      // The handler will close the modal if the topic is valid (not duplicate)
      await onTopicSelected(selectedTopic, week);
      // Only close if handler didn't return early (no duplicate found)
      // The handler will close the modal itself if successful
    }
  };

  /**
   * Handle modal close
   */
  const handleClose = () => {
    setSearchTerm('');
    setError(null);
    if (onHide) {
      onHide();
    }
  };

  /**
   * Handle backdrop click
   */
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  /**
   * Format date safely
   */
  const formatDate = (dateValue) => {
    if (!dateValue) return '';
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString();
    } catch (e) {
      return '';
    }
  };

  if (!show) return null;

  return (
    <div className="modal fade show react-modal-fix topic-search-modal" style={{ display: 'block' }} onClick={handleBackdropClick}>
      <div className="modal-dialog modal-sm modal-dialog-centered" style={{ maxWidth: '400px' }}>
        <div className="modal-content">
          
          {/* Modal Header */}
          <div className="modal-header">
            <h4 className="modal-title">
              Select Topic for Week {week?.WeekNo}{week?.StartDate ? ` (${formatDate(week.StartDate)})` : ''}
            </h4>
            <button 
              type="button" 
              className="btn-close" 
              onClick={handleClose}
              aria-label="Close"
            ></button>
          </div>

          {/* Modal Body */}
          <div className="modal-body">

            {/* Search Input */}
            <div className="mb-3">
              <label htmlFor="topicSearch" className="form-label">Search Topics:</label>
              <input
                type="text"
                id="topicSearch"
                className="form-control"
                placeholder="Type to search topics by name or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
            </div>

            {/* Loading State */}
            {loading && (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading topics...</span>
                </div>
                <p className="mt-2">Loading available topics...</p>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="alert alert-danger">
                <h6>Error Loading Topics</h6>
                <p className="mb-0">{error}</p>
              </div>
            )}

            {/* Topics List */}
            {!loading && !error && (
              <div className="topics-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {filteredTopics.length === 0 ? (
                  <div className="text-center py-4">
                    <i className="fas fa-search fa-2x text-muted"></i>
                    <p className="mt-2 text-muted">
                      {searchTerm ? `No topics found matching "${searchTerm}"` : 'No topics available'}
                    </p>
                  </div>
                ) : (
                  <div className="list-group">
                    {filteredTopics.map((topic, index) => (
                      <button
                        key={topic.ID || index}
                        type="button"
                        className="list-group-item list-group-item-action"
                        onClick={() => handleTopicSelect(topic)}
                        style={{ textAlign: 'left' }}
                      >
                        <div className="d-flex w-100 justify-content-between">
                          <h6 className="mb-1">{topic.name}</h6>
                          <small className="text-muted">ID: {topic.ID}</small>
                        </div>
                        {topic.description && (
                          <p className="mb-1 text-muted">{topic.description}</p>
                        )}
                        <small className="text-success">
                          <i className="fas fa-plus-circle me-1"></i>
                          Click to add to week
                        </small>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Results Count */}
            {!loading && !error && filteredTopics.length > 0 && (
              <div className="mt-3">
                <small className="text-muted">
                  Showing {filteredTopics.length} of {topics.length} topics
                  {searchTerm && ` matching "${searchTerm}"`}
                </small>
              </div>
            )}

          </div>

          {/* Modal Footer */}
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={handleClose}>
              Cancel
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default TopicSearchModal; 